import cloneDeep from 'lodash/cloneDeep.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import head from 'lodash/head.js';
import isEmpty from 'lodash/isEmpty.js';
import isNull from 'lodash/isNull.js';
import orderBy from 'lodash/orderBy.js';
import set from 'lodash/set.js';
import toArray from 'lodash/toArray.js';
import numeral from 'numeral';

import Course from '@/models/Course.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import QuizAttemptHelper from '@/store/utilities/QuizAttemptHelper.js';
import { applyActivityPointsUpdates } from '@/utilities/coursePointsUpdated.js';
import { resolveLowestCriteria } from '@/utilities/gradebookFailingThreshold.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const totalScore = (submissions, prop) => {
    if (isEmpty(submissions)) return 0;

    const data = submissions.reduce((initial, submission) => {
        submission = { [prop]: (initial[prop] || 0) + (submission[prop] || 0) };

        return submission;
    });

    return data[prop];
};

const buildZonedMoment = (date, timezone) => {
    const zonedMoment = moment.utc(date);

    if (!zonedMoment.isValid()) return null;

    if (timezone && moment.tz.zone(timezone)) {
        zonedMoment.tz(timezone);
    }

    return zonedMoment;
};

const pastInitialDate = (timezone, initialDate, targetDate) => {
    const initial = buildZonedMoment(initialDate, timezone);
    const target = buildZonedMoment(targetDate, timezone);

    if (!initial || !target) return false;

    return initial.isBefore(target);
};

const checkSubmissionId = grade => isNull(grade.activity_submission_id);

const findSubmission = (grade, posted_grade) => {
    if (checkSubmissionId(posted_grade)) {
        return Number(grade.activity_id) === Number(posted_grade.activity_id);
    }

    return Number(grade.activity_submission_id) === Number(posted_grade.activity_submission_id);
};

const GRADEBOOK_ICON_STYLE = {
    danger: {
        className: 'has-text-danger',
        type: 'is-danger',
    },
    info: {
        className: 'has-text-info',
        type: 'is-info',
    },
    warning: {
        className: 'has-text-warning',
        type: 'is-warning',
    },
    muted: {
        className: 'has-text-grey',
        type: 'is-dark',
    },
};

const applyStatusIconStyling = grade => {
    if (!grade) return grade;

    const status = Array.isArray(grade.status) ? grade.status : [];

    if (status.includes('Needs Grading')) {
        grade.icon = 'exclamation-circle';
        grade.icon_color = GRADEBOOK_ICON_STYLE.danger.className;
        grade.icon_type = GRADEBOOK_ICON_STYLE.danger.type;
        return grade;
    }

    if (status.includes('In Progress')) {
        grade.icon = 'sync';
        grade.icon_color = GRADEBOOK_ICON_STYLE.info.className;
        grade.icon_type = GRADEBOOK_ICON_STYLE.info.type;
        return grade;
    }

    if (
        status.includes('No Submission') ||
        status.includes('Pending Submission') ||
        status.includes('View Grade for Non Submission')
    ) {
        grade.icon = 'minus-circle';
        grade.icon_color = GRADEBOOK_ICON_STYLE.muted.className;
        grade.icon_type = GRADEBOOK_ICON_STYLE.muted.type;
        return grade;
    }

    if (status.includes('Late Submission') && !grade.icon) {
        grade.icon = 'exclamation-triangle';
        grade.icon_color = GRADEBOOK_ICON_STYLE.warning.className;
        grade.icon_type = GRADEBOOK_ICON_STYLE.warning.type;
    }

    return grade;
};

export default {
    namespaced: true,

    state: {
        gradebook: {
            filterColumns: {
                runningPercentage: { title: 'Running %', display: true },
                percentage: { title: '%', display: true },
                grade: { title: 'Grade', display: true },
                comments: { title: 'Comments', display: true },
                publish: { title: 'Publish', display: true },
                admin: { title: 'Admin', display: true },
            },

            filterStudents: {
                removed: { title: 'Include Removed', display: true },
            },
        },

        miscFilters: {
            courseId: null,
            horizontalScroll: 0,
            searchValue: {
                activity: '',
                student: '',
            },
        },

        submissions: {},
        hardReload: false,
        isLoading: null,
        loaders: [],
        parent: null,
        cancelTokens: [],
        selections: [],
    },

    getters: {
        isEmpty: state => payload => {
            if (isEmpty(state.submissions)) return true;

            if (has(state.submissions, payload.activityId)) {
                return isEmpty(state.submissions[payload.activityId]);
            }

            return true;
        },

        isLoading: state => state.isLoading,

        isLoadingId: state => id => state.loaders.includes(id),

        get:
            state =>
            ({ id, property }) => {
                if (!id) return state[property];

                return state[property][id];
            },

        getSubmission:
            (state, getters) =>
            ({ activityId, submissionId }) => {
                const submissions = getters.get({
                    property: 'submissions',
                    id: activityId,
                });

                return submissions[submissionId];
            },
    },

    actions: {
        async activitySubmissions({ getters, dispatch, commit }, payload) {
            try {
                const { activityId, creator, submissionId, includeGradeAttachments } = payload;

                let submissions = toArray(
                    getters.get({
                        property: 'submissions',
                        id: activityId,
                    })
                );

                const activity = await dispatch('fetchActivity', payload);

                const isDifferentUser = submissions.some(submission => submission.created_by !== creator);

                if (isEmpty(submissions) || isDifferentUser) {
                    const queryBuilder = new QueryBuilder('activity-submissions')
                        .include([
                            'user',
                            'submittable',
                            'gradebookGrades',
                            'gradebookGrades.criterias',
                            'attemptAuthorizations.creator',
                        ])
                        .append('graded_at');

                    if (activity.activity_type_key !== 'file-submission') {
                        queryBuilder.filter('id', submissionId);
                    } else {
                        queryBuilder.filter('activity_id', activityId);
                    }

                    if (creator) {
                        queryBuilder.filter('created_by', creator);
                    }

                    if (includeGradeAttachments) {
                        queryBuilder.include('gradebookGrades.media');
                    }

                    const response = await axios.get(queryBuilder.toUrl());
                    submissions = response.data.data;
                }

                if (isEmpty(submissions)) {
                    return false;
                }

                const processedSubmissions = submissions.map(submission => {
                    submission.is_editing = true;
                    submission.saved_grade = null;

                    if (!submission?.attempt_authorizations) {
                        submission.attempt_authorizations = [];
                    }

                    if (!isEmpty(submission.gradebook_grades)) {
                        submission.is_editing = false;

                        submission.gradebook_grades.sort((a, b) => b.id - a.id);

                        const [latestGrade] = submission.gradebook_grades;

                        return {
                            ...submission,
                            saved_grade: latestGrade,
                            gradebook_grade_id: latestGrade.id,
                            criterias: latestGrade.criterias,
                            points: latestGrade.points,
                            comments: latestGrade.comments,
                        };
                    }

                    return submission;
                });

                await commit('HYDRATE_SUBMISSIONS', {
                    id: activityId,
                    submissions: processedSubmissions,
                });

                if (['quiz', 'survey'].includes(activity.activity_type_key)) {
                    await dispatch('hydrateQuizGrader', activityId);
                }

                return true;
            } catch (error) {
                console.error('Error in activitySubmissions action:', error);
                return false;
            }
        },

        async fetchActivity(context, payload) {
            const queryBuilder = new QueryBuilder(`activities/${payload.activityId}`).include([
                'rubric',
                'activityType',
                'gradingScheme',
            ]);

            const { data } = await axios.get(queryBuilder.toUrl());

            const activity = data.data;

            await context.dispatch('Activities/hydrateActivity', { payload: activity }, { root: true });

            return activity;
        },

        async hydrateQuizGrader(context, activityId) {
            const helper = new QuizAttemptHelper({ store: context });

            const items = toArray(context.state.submissions[activityId]);

            await Promise.all(
                items.map(async item => {
                    const { quiz_attempt_id } = item.submittable;

                    if (!quiz_attempt_id) return;

                    const submission = cloneDeep(
                        context.getters.getSubmission({
                            activityId,
                            submissionId: item.id,
                        })
                    );

                    const quiz_review = `/api/quiz-attempts/${quiz_attempt_id}/instructor-review`;

                    const quiz_attempt = await axios.get(quiz_review).then(resp => {
                        return resp.data.data;
                    });

                    if (!has(item.submittable, 'quiz_attempt')) {
                        submission.submittable.quiz_attempt = {};
                    }

                    submission.submittable.quiz_attempt = {
                        ...submission.submittable.quiz_attempt,
                        ...quiz_attempt.scoring,
                    };

                    submission.submittable.quiz_attempt.quiz_attempt_questions = helper.parseSelectedAnswers(
                        quiz_attempt.quiz_attempt_questions
                    );

                    context.commit('EDIT_SUBMISSION', {
                        activity_id: activityId,
                        submission_id: item.id,
                        submission,
                    });
                })
            );
        },

        async fetchCourseGrades(context, { courseId }) {
            const queryBuilder = new QueryBuilder('course-grades');
            queryBuilder.filter('course_id', courseId);
            queryBuilder.raw({ per_page: 100 });
            queryBuilder.append('is_passing');
            queryBuilder.append('has_been_remediated');

            const grades = await axios.get(queryBuilder.toUrl());

            return orderBy(grades.data.data, 'created_at', 'asc');
        },

        async fetchGradebook(context, { id, courseId }) {
            context.commit('TOGGLE_LOADING');

            const payload = await axios.get(`/api/gradebook-information/${id}?filter[course_id]=${courseId}`);

            const gradebook = payload.data.data;

            await Course.insert({ data: gradebook.course });

            gradebook.grading_scheme = gradebook.course.grading_scheme;

            await context.dispatch('mergeCourseGrades', { gradebook });

            await context.dispatch('assignActivityTotals', { gradebook });

            await context.dispatch('parseGradebookItems', { gradebook });

            await context.dispatch('ActivityGraderNavigation/sortGradebookStudents', gradebook, { root: true });

            await context.commit('HYDRATE_GRADEBOOK', {
                id,
                payload: gradebook,
            });

            context.commit('TOGGLE_LOADING');

            return payload.data;
        },

        async fetchRubrics(context, { payload }) {
            const arr = [];

            payload.activities.forEach(activity => arr.push(activity.id));

            const activity_ids = arr.join(',');

            const queryBuilder = new QueryBuilder('rubrics');

            queryBuilder.filter('activity_id', activity_ids);

            queryBuilder.raw({ per_page: 100 });

            const url = queryBuilder.toUrl();

            const { data } = await axios.get(url);

            return data.data;
        },

        async cancelRequest(context) {
            if (context.state.cancelTokens.length) {
                await context.state.cancelTokens.map(item => {
                    item.cancel();
                });
            }
            return (context.state.cancelTokens = []);
        },

        async fetchStudentGrade(context, { courseId, userId, profileGrades = false }) {
            // Set the loading flag explicitly (not a toggle) so a rejection in any of the
            // calls below can't leave the page stuck loading and permanently blank.
            context.commit('EDIT_FIELD', { field: 'isLoading', value: true });

            const source = axios.CancelToken.source();

            context.commit('SET_CANCEL_SOURCE', source);

            const grade_api = axios.get(
                new QueryBuilder('gradebook-grades')
                    .include(['criterias', 'gradebook', 'media'])
                    .raw({ per_page: 100, course_id: courseId })
                    .filter('user_id', userId)
                    .toUrl(),
                { cancelToken: source.token }
            );

            const activities_api = new QueryBuilder('activities')
                .include(['rubric', 'contentable'])
                .filters([
                    { key: 'course_id', value: courseId },
                    { key: 'gradable', value: 1 },
                ])
                .raw({ per_page: 100, profile_grades: profileGrades })
                .sort('due_at')
                .toUrl();

            const activities_call = axios.get(activities_api, { cancelToken: source.token }).then(async resp => {
                const quiz_activities = resp.data.data.filter(item => item.activity_type_key === 'quiz');

                await Promise.all(
                    quiz_activities.map(async item => {
                        const quizId = item.activity_content?.quiz_id;

                        // Guard a missing quiz id, and isolate a single quiz fetch failure
                        // (e.g. a 403/404) so it can't reject the whole load and leave the
                        // student's grade page blank.
                        if (!quizId) {
                            return;
                        }

                        try {
                            const quiz = await axios
                                .get(`/api/quizzes/${quizId}?append=points_possible`, {
                                    cancelToken: source.token,
                                })
                                .then(payload => payload.data.data);

                            const index = resp.data.data.findIndex(x => x.id === item.id);

                            resp.data.data[index].quiz = quiz;
                        } catch (error) {
                            if (axios.isCancel(error)) {
                                throw error;
                            }
                            // Skip this quiz's points rather than failing the entire page.
                        }
                    })
                );

                return resp.data.data;
            });

            const submissions_api = axios.get(
                new QueryBuilder('activity-submissions')
                    .include('creator')
                    .filters([
                        { key: 'created_by', value: userId },
                        { key: 'course_id', value: courseId },
                    ])
                    .raw({ per_page: 100 })
                    .toUrl(),
                { cancelToken: source.token }
            );

            const course_grade_api = axios.get(
                new QueryBuilder('course-grades')
                    .include('gradingScheme')
                    .filters([
                        { key: 'user_id', value: userId },
                        { key: 'course_id', value: courseId },
                    ])
                    .append('has_been_remediated')
                    .sort('created_at', 'desc')
                    .toUrl(),
                { cancelToken: source.token }
            );

            try {
                let course = context.rootGetters['Courses/get'](courseId);

                if (!course) {
                    const courseApi = profileGrades
                        ? new QueryBuilder(`courses/${courseId}`).raw({ profile_grades: 1 })
                        : new QueryBuilder(`courses/${courseId}`);
                    course = await axios.get(courseApi.toUrl()).then(resp => resp.data.data);
                }

                await Promise.all([grade_api, activities_call, submissions_api, course_grade_api]).then(async resp => {
                    const grade = await context.dispatch('parseGrades', {
                        grades: resp[0].data.data.filter(
                            item =>
                                Number(item.user_id) === Number(userId) &&
                                Number(item.gradebook.course_id) === Number(courseId)
                        ),
                        activities: resp[1],
                        submissions: resp[2].data.data,
                        course: resp[3].data.data[0],
                    });

                    context.commit('HYDRATE_GRADEBOOK', {
                        id: `${userId}-${courseId}`,
                        payload: {
                            ...grade,
                            course_settings: course.course_settings,
                        },
                    });
                });
            } catch (error) {
                // A failed load must not leave the page stuck loading and blank; reset the
                // flag in finally and let the view render its empty/error state. Cancels
                // (route change) are expected and ignored.
                if (!axios.isCancel(error)) {
                    console.error('Failed to load student grades', error);
                }
            } finally {
                context.commit('EDIT_FIELD', { field: 'isLoading', value: false });
            }
        },

        async fetchSubmittedData(context, { routes, type }) {
            const result = [];
            const promises = [];

            routes.forEach(route => {
                promises.push(axios.get(route));
            });

            await Promise.all(promises).then(resp => {
                resp.forEach(resp => {
                    result.push(resp.data.data);
                });
            });

            if (type) {
                context.commit('ADD_THREAD_DATA', { payload: result, type });
            }

            return result;
        },

        mergeCourseGrades(context, { gradebook }) {
            if (gradebook?.course_grades) {
                gradebook.students.forEach(student => {
                    const courseGrades = orderBy(
                        gradebook.course_grades.filter(grade => grade.user_id === student.user_id),
                        ['created_at'],
                        ['desc']
                    );

                    student.course_grade = head(courseGrades);
                    student.course_grades = courseGrades;
                    student.has_projected_grade = false;
                    student.course_grade_comments = head(courseGrades)?.comments;
                });
            }
        },

        assignActivityTotals(context, { gradebook, rubrics }) {
            gradebook.activities.forEach((activity, index) => {
                activity.order = index;

                activity.activity_total = activity.points_possible;
            });
        },

        parseGradebookItems(context, { gradebook }) {
            const { activities } = gradebook;
            const { students } = gradebook;
            const today = gradebook.current_date_time;
            const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

            // Determine if a student submission is missing

            activities.forEach(activity => {
                context.dispatch(
                    'Activities/hydrateActivity',
                    { payload: activity },
                    {
                        root: true,
                    }
                );

                students.forEach(student => {
                    student.edit_total_points = false;

                    if (!student.user.grades.some(grade => grade.activity_id === activity.id)) {
                        student.user.grades.push({
                            activity_id: activity.id,
                            criterias: [],
                            due_date: activity.activity_settings.due_at,
                            icon: 'minus-circle',
                            icon_color: 'has-text-grey',
                            order: activity.order,
                            points: null,
                            status: ['No Submission'],
                            activity,
                        });
                    }

                    student.user.grades.forEach(grade => {
                        if (Number(grade.activity_id) === Number(activity.id)) {
                            grade.rubric = !isEmpty(activity.rubric);

                            if (grade.automatic && !grade.points) {
                                grade.points = null;
                            }

                            if (grade.automatic && !grade.points && grade.id) {
                                grade.points = 0;
                            }

                            if (!has(grade, 'status')) {
                                grade.status = [];
                                grade.due_date = activity.activity_settings.due_at;
                            }

                            if (
                                has(activity, 'activity_settings.points_possible') &&
                                ['view-information', 'acknowledge-information'].some(
                                    type => type === activity.activity_type_key
                                )
                            ) {
                                if (activity.activity_settings.auto_grade && !grade.status.includes('No Submission')) {
                                    grade.status.push('Auto Graded');
                                }
                            }

                            if (has(activity.activity_settings, 'auto_grade')) {
                                grade.auto_grade = activity.activity_settings.auto_grade;
                            }

                            if (
                                isNull(grade.activity_submission_id) &&
                                (!pastInitialDate(timezone, today, grade.due_date) || isNull(grade.due_date))
                            ) {
                                grade.status.push('View Grade for Non Submission');
                                grade.no_submission = true;
                                grade.past_due = true;
                            }
                        }
                    });
                });
            });

            // Assign 'Needs Grading' if student has a submission that needs grading

            activities.forEach(activity => {
                let sortedSubmissions = activity.submissions;
                if (activity.contentable_type === 'App\\Models\\ActivityContent\\QuizActivity') {
                    sortedSubmissions = [...activity.submissions].sort(
                        (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
                    );
                }

                sortedSubmissions.forEach(submission => {
                    if (!submission?.gradebook_grades) {
                        submission.gradebook_grades = [];
                    }

                    students.forEach(student => {
                        const check = grade =>
                            grade.activity_id === submission.activity_id && student.user.id === submission.created_by;
                        const gradeIndex = student.user.grades.findIndex(grade => check(grade));

                        const pastDue = !pastInitialDate(timezone, today, activity.activity_settings.due_at);
                        const markedComplete = !!get(submission, 'marked_completed_at');
                        const maxAttemptsAllowed = activity.activity_settings.max_attempts_allowed;

                        if (gradeIndex >= 0) {
                            const grade = student.user.grades;

                            const isQuizActivity =
                                activity.contentable_type === 'App\\Models\\ActivityContent\\QuizActivity';

                            if (grade[gradeIndex].activity_submission_id === submission.id) {
                                submission.gradebook_grades.push(grade[gradeIndex]);

                                // Only assign quiz points if this is an automatic grade and points aren't already set
                                if (
                                    isQuizActivity &&
                                    submission.points !== null &&
                                    submission.points !== undefined &&
                                    grade[gradeIndex].automatic &&
                                    (grade[gradeIndex].points === null || grade[gradeIndex].points === undefined)
                                ) {
                                    grade[gradeIndex].points = submission.points;
                                }
                            }

                            if (grade[gradeIndex].status && grade[gradeIndex].status.includes('No Submission')) {
                                if (pastDue || maxAttemptsAllowed === 1 || markedComplete) {
                                    grade[gradeIndex].status = ['Needs Grading'];
                                    grade[gradeIndex].icon = 'exclamation-circle';
                                    grade[gradeIndex].icon_color = GRADEBOOK_ICON_STYLE.danger.className;
                                    grade[gradeIndex].icon_type = GRADEBOOK_ICON_STYLE.danger.type;
                                } else {
                                    grade[gradeIndex].status = ['In Progress'];
                                    grade[gradeIndex].icon = 'sync';
                                    grade[gradeIndex].icon_color = GRADEBOOK_ICON_STYLE.info.className;
                                    grade[gradeIndex].icon_type = GRADEBOOK_ICON_STYLE.info.type;
                                }
                                grade[gradeIndex].activity_submission_id = submission.id;
                                grade[gradeIndex].grading_scheme_id = activity.grading_scheme_id;
                            }

                            if (
                                pastInitialDate(timezone, activity.activity_settings.due_at, submission.created_at) &&
                                !grade[gradeIndex].status.includes('Late Submission')
                            ) {
                                grade[gradeIndex].late = true;
                                grade[gradeIndex].status.push('Late Submission');
                            }

                            applyStatusIconStyling(grade[gradeIndex]);
                        }
                    });
                });

                context.commit('HYDRATE_SUBMISSIONS', {
                    id: activity.id,
                    submissions: activity.submissions,
                });
            });

            // Attach status object to student to display status on student name in gradebook table

            students.forEach(student => {
                student.activity_status = {};

                if (isEmpty(get(student, 'course_grade'))) {
                    student.has_projected_grade = true;
                }

                student.save_status = {
                    is_loading: false,
                    is_saved: false,
                };

                student.user.grades.forEach(grade => {
                    applyStatusIconStyling(grade);

                    if (
                        (grade.status.includes('No Submission') || grade.status.includes('Needs Grading')) &&
                        !pastInitialDate(timezone, today, grade.due_date)
                    ) {
                        student.has_status = true;
                    }

                    if (
                        grade.status.includes('No Submission') &&
                        !isNull(grade.due_date) &&
                        pastInitialDate(timezone, today, grade.due_date)
                    ) {
                        grade.not_due = true;
                    }

                    if (!isNull(grade.due_date)) {
                        if (pastInitialDate(timezone, today, grade.due_date)) {
                            grade.is_not_yet_due = true;
                        } else {
                            grade.is_past_due = true;
                        }
                    }

                    if (
                        grade.status.includes('No Submission') &&
                        (!pastInitialDate(timezone, today, grade.due_date) || isNull(grade.due_date))
                    ) {
                        grade.no_submission = true;
                        grade.past_due = true;
                    }
                });
            });

            // Sort grades and assign totals

            activities.forEach(activity => {
                students.forEach(student => {
                    student.edit_total_points = false;
                    student.running_course_points = null;

                    student.user.grades.forEach(grade => {
                        if (grade.activity_id === activity.id) {
                            grade.order = activity.order;
                            grade.activity_total = activity.activity_total;
                        }
                    });

                    student.user.grades.sort((a, b) => (a.order > b.order ? 1 : -1));

                    student.total_points = student.course_grade
                        ? student.course_grade.points
                        : totalScore(student.user.grades, 'points');

                    student.total_points_overridden = student.course_grade ? student.course_grade.overridden : false;
                });
            });

            // Sort students by last name

            students.sort((student1, student2) => {
                if (student1.user.last_name === student2.user.last_name) {
                    if (student1.user.first_name < student2.user.first_name) return -1;
                    if (student1.user.first_name > student2.user.first_name) return 1;
                }

                if (student1.user.last_name < student2.user.last_name) return -1;
                if (student1.user.last_name > student2.user.last_name) return 1;

                return 0;
            });
        },

        async parseGrades(context, { grades, activities, submissions, course }) {
            let course_grade;

            const timezone = context.rootGetters['InstitutionSettings/get']('timezone');

            activities = activities.filter(item => {
                if (item.rubric) return true;

                if (get(item, 'quiz')) return true;

                if (
                    get(item.activity_settings, 'points_possible') ||
                    get(item.activity_settings, 'points_possible') === 0
                ) {
                    return true;
                }

                return false;
            });

            if (submissions.length > 1) {
                submissions.sort((a, b) => {
                    return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
                });
            }

            const getActivityTotal = activity => {
                const type = activity.activity_type;
                const rubric = get(activity, 'rubric');
                const hasRubric = !isEmpty(rubric);
                const pointsPossible = get(activity, 'activity_settings.points_possible');
                const hasPointsPossible = pointsPossible || pointsPossible === 0;

                if (hasPointsPossible && !hasRubric) {
                    return Number(pointsPossible);
                }

                if (type === 'quiz') {
                    return Number(activity.quiz.points_possible);
                }

                return Number(get(rubric, 'max_value'));
            };

            // Assign 'No Submission' if student grade is missing

            activities.forEach((activity, order) => {
                const activity_total = getActivityTotal(activity);

                if (!grades.some(grade => Number(grade.activity_id) === Number(activity.id))) {
                    grades.push({
                        activity_id: activity.id,
                        criterias: [],
                        order,
                        points: 0,
                        status: ['Pending Submission'],
                    });
                }

                const index = grades.findIndex(item => Number(item.activity_id) === Number(activity.id));

                if (index < 0) return;

                // Bring nested property to root level for easy access
                // later used when parsing grading scheme

                activity.points = activity_total;

                // Interpret null grade points as a 0

                if (grades[index].automatic && !grades[index].points) {
                    grades[index].points = 0;
                }

                if (!has(grades[index], 'status')) {
                    grades[index].status = [];
                    grades[index].due_date = activity.activity_settings.due_at;
                }

                if (has(grades[index], 'id')) {
                    grades[index].status.push('Graded');
                }

                // Parse activity status

                const submission = submissions.findIndex(
                    submission => Number(submission.activity_id) === Number(grades[index].activity_id)
                );

                if (submission >= 0) {
                    if (grades[index].status.includes('Pending Submission')) {
                        grades[index].status = ['Pending Grade'];
                        grades[index].activity_submission_id = submissions[submission].id;
                    }

                    grades[index].submission_date = submissions[submission].created_at;

                    if (
                        grades[index].due_date &&
                        !pastInitialDate(timezone, submissions[submission].created_at, grades[index].due_date)
                    ) {
                        grades[index].late = true;
                        grades[index].status.push('Submitted Late');
                    }
                }

                // Assign order and activity object for easy access to nested properties

                grades[index].order = order;

                grades[index].activity = {
                    title: activity.title,
                    due_at: activity.activity_settings.due_at,
                    max_value: activity_total,
                    type: activity.activity_type_key,
                    minimum_passing_score: activity.activity_settings.minimum_passing_score,
                };

                // Account manual criteria rating

                if (!isEmpty(activity.rubric)) {
                    grades[index].criterias.forEach(item => {
                        const index = activity.rubric.criterias.findIndex(
                            criteria => criteria.id === item.rubric_criteria_id
                        );

                        if (index >= 0) {
                            item.title = activity.rubric.criterias[index].title;

                            activity.rubric.criterias[index].criteria_ratings.forEach(rating => {
                                if (rating.value === item.score && !item.reason) {
                                    item.description = rating.description;
                                    item.label = rating.label;
                                }
                            });
                        }
                    });
                }
            });

            if (course && course.grading_scheme) {
                const requiredActivities = activities.filter(x => !x.activity_settings.is_extra_credit);

                course.total = totalScore(requiredActivities, 'points');

                course.percentage = course.total ? numeral(course.points / course.total).format('0.00') : 1;

                course.grading_scheme.criterias.reduce((initial, criteria, index, criterias) => {
                    if (course.percentage < initial.minimum && course.percentage >= criteria.minimum)
                        course.grade = criteria;

                    if (course.percentage >= initial.minimum && index === 1) course.grade = initial;

                    if (course.percentage <= criteria.minimum && index === criterias.length - 1)
                        course.grade = criteria;

                    return criteria;
                });

                course_grade = [
                    {
                        label: 'Points',
                        description: `${course.points} out of ${course.total}`,
                        disabled: false,
                    },
                    {
                        label: 'Percentage',
                        description: `${(course.percentage * 100).toFixed(2)} %`,
                        disabled: false,
                    },
                    {
                        label: 'Grade',
                        description: `${course.grade ? course.grade.name : 'N/A'}`,
                        disabled: false,
                        has_been_remediated: course?.has_been_remediated,
                        created_at: course?.created_at,
                    },
                    {
                        label: 'Comments',
                        description: course.comments ? course.comments : 'N/A',
                        disabled: false,
                    },
                ];
            }

            if (!course) {
                course_grade = [
                    { label: 'Running Points', description: 'N/A', disabled: true },
                    {
                        label: 'Running Percentage',
                        description: 'N/A',
                        disabled: true,
                    },
                    { label: 'Grade', description: 'N/A', disabled: true },
                    { label: 'Comments', description: 'N/A', disabled: true },
                ];
            }

            // Sort Grades

            if (grades) {
                grades.sort((a, b) => (a.order > b.order ? 1 : -1));
            }

            // TODO: Investigate why grades may exist for activities
            //  that are not in the initial payload

            const filteredGrades = grades.filter(grade => {
                const index = activities.findIndex(activity => activity.id === grade.activity_id);

                return index >= 0;
            });

            grades = filteredGrades;

            return { grades, activities, submissions, course_grade };
        },

        async submit(context, { payload, loader, parent }) {
            const type = has(payload, 'id') ? 'put' : 'post';
            const url = `/api/gradebook-grades${payload.id ? `/${payload.id}?include=media` : ''}`;

            try {
                const activity_grade = await axios[type](url, payload).then(resp => resp.data.data);

                await context.dispatch('updateSubmission', {
                    submission_id: payload.activity_submission_id,
                    activity_grade,
                });

                if (parent === 'gradebook') {
                    await context.dispatch('updateGradebook', {
                        activity_grade,
                    });
                }

                context.commit('TOGGLE_EDITING', {
                    activity_id: activity_grade.activity_id,
                    submission_id: payload.activity_submission_id,
                });

                loader.close();

                return activity_grade;
            } catch (error) {
                loader.close();
                throw error;
            }
        },

        updateSubmission(context, { submission_id, activity_grade }) {
            const submission = context.getters.getSubmission({
                activityId: activity_grade.activity_id,
                submissionId: submission_id,
            });

            submission.gradebook_grade_id = activity_grade.id;

            submission.criterias = activity_grade.criterias;

            submission.comments = activity_grade.comments;

            submission.points = activity_grade.points;

            submission.saved_grade = activity_grade;

            context.commit('EDIT_SUBMISSION', {
                activity_id: activity_grade.activity_id,
                submission_id,
                submission,
            });
        },

        updateGradebook(context, { activity_grade }) {
            const noSubmissionId = checkSubmissionId(activity_grade);

            const gradebook = context.getters.get({
                id: activity_grade.gradebook_id,
                property: 'gradebook',
            });

            if (!gradebook) return;

            const student_index = gradebook.students.findIndex(student => student.user_id === activity_grade.user_id);

            const grade_index = gradebook.students[student_index].user.grades.findIndex(grade =>
                findSubmission(grade, activity_grade)
            );

            if (student_index >= 0 && grade_index >= 0) {
                const student = gradebook.students[student_index];
                const grade = student.user.grades[grade_index];

                if (grade.status && !noSubmissionId) activity_grade.status = [];

                if (grade.status && noSubmissionId) {
                    activity_grade.no_submission = true;
                    activity_grade.past_due = true;
                    activity_grade.status = ['View Grade for Non Submission'];
                }

                if (grade.late) {
                    activity_grade.late = grade.late;
                    activity_grade.status.push('Late Submission');
                }

                applyStatusIconStyling(activity_grade);

                context.commit('EDIT_STUDENT_GRADE', { student_index, grade_index, activity_grade });
            }
        },

        async deleteFromGradebook(context, { grade }) {
            const userId = get(grade, 'user_id') || get(grade, 'creator.id') || get(grade, 'student.user_id');

            let id = get(grade, 'gradebook_grade_id');

            if (has(grade, 'activity_submission_id') && !has(grade, 'gradebook_grade_id')) {
                id = get(grade, 'id');
            }

            if (!id) {
                throw new Error('Gradebook grade ID is required to delete a grade');
            }

            const { data } = await axios.delete(`/api/gradebook-grades/${id}`);

            const noSubmissionId = checkSubmissionId(grade);

            const gradebook = context.getters.get({
                id: grade.gradebook_id || grade.course_id,
                property: 'gradebook',
            });

            if (gradebook) {
                context.commit(
                    'ActivityGraderNavigation/EDIT',
                    {
                        activity: grade.activity_id,
                        user: userId,
                        payload: [{ key: 'points', value: null }],
                    },
                    { root: true }
                );
            }

            if (!gradebook) return;

            if (!noSubmissionId) {
                const submission = context.getters.getSubmission({
                    activityId: grade.activity_id,
                    submissionId: grade.activity_submission_id,
                });

                submission.gradebook_grades = [];
                submission.gradebook_grade_id = null;
                submission.criterias = [];
                submission.points = null;
                submission.comments = null;
            }

            if (!gradebook?.students) {
                // eslint-disable-next-line consistent-return
                return data;
            }

            const studentIndex = gradebook.students.findIndex(student => Number(student.user_id) === Number(userId));

            const gradeIndex = gradebook.students[studentIndex].user.grades.findIndex(studentGrade =>
                findSubmission(studentGrade, grade)
            );

            if (studentIndex >= 0 && gradeIndex >= 0) {
                const student = gradebook.students[studentIndex];
                const studentGrade = student.user.grades[gradeIndex];

                studentGrade.points = null;
                studentGrade.comments = null;

                if (!noSubmissionId) {
                    studentGrade.status = ['Needs Grading'];

                    if (studentGrade.late) {
                        studentGrade.status.push('Late Submission');
                    }

                    studentGrade.icon = 'exclamation-circle';
                    studentGrade.icon_color = GRADEBOOK_ICON_STYLE.danger.className;
                    studentGrade.icon_type = GRADEBOOK_ICON_STYLE.danger.type;
                    studentGrade.criterias = [];
                }

                if (noSubmissionId) {
                    studentGrade.icon = 'minus-circle';
                    studentGrade.icon_color = GRADEBOOK_ICON_STYLE.muted.className;
                    studentGrade.icon_type = GRADEBOOK_ICON_STYLE.muted.type;
                    studentGrade.no_submission = true;
                    studentGrade.past_due = true;
                    studentGrade.status = ['No Submission'];
                }

                applyStatusIconStyling(studentGrade);

                gradebook.students[studentIndex].user.grades[gradeIndex] = studentGrade;
                gradebook.students[studentIndex].total_points = totalScore(student.user.grades, 'points');
            }

            // eslint-disable-next-line consistent-return
            return data;
        },

        async resetGrade({ commit }, { submission }) {
            submission.is_editing = false;
            submission.gradebook_grade_id = submission.saved_grade.id;
            submission.criterias = submission.saved_grade.criterias;
            submission.points = submission.saved_grade.points;
            submission.comments = submission.saved_grade.comments;

            await commit('EDIT_SUBMISSION', {
                activity_id: submission.activity_id,
                submission_id: submission.id,
                submission,
            });
        },

        async submitCourseGrade(context, { gradebook, student, publishDate, manualFail = false }) {
            const api = '/api/course-grades';
            const type = student?.course_grade ? 'put' : 'post';
            const url = type === 'put' ? `${api}/${student.course_grade.id}` : api;
            const gradingScheme = gradebook?.gradingScheme || gradebook?.grading_scheme || null;
            const gradingCriterias = gradingScheme?.criterias || [];
            const lowestGradingCriteria = resolveLowestCriteria(gradingCriterias);
            const studentCriteriaId = get(student, 'grading_scheme.id');
            const gradingSchemeId = get(student, 'grading_scheme.grading_scheme_id') || gradingScheme?.id;

            const payload = {
                comments: student.course_grade_comments,
                course_id: gradebook.courseId || gradebook.course.id,
                grading_scheme_criteria_id: manualFail
                    ? lowestGradingCriteria?.id || studentCriteriaId
                    : studentCriteriaId,
                grading_scheme_id: gradingSchemeId,
                overridden: student.total_points_overridden || false,
                override_reason: student.override_reason || null,
                points: student.total_points && !manualFail ? student.total_points : 0,
                user_id: student.user.id,
            };

            if (publishDate) {
                payload.published_at = publishDate;
            }

            await axios[type](url, payload, {
                errorHandle: false,
            });
        },

        setLoading({ commit }, value) {
            commit('EDIT_FIELD', { field: 'isLoading', value });
        },
    },

    mutations: {
        EDIT_STUDENT_COURSE_GRADE(state, { gradebookId, student }) {
            const studentIndex = state.gradebook[gradebookId].students.findIndex(
                item => item.user_id === student.user_id
            );

            if (studentIndex >= 0) {
                delete state.gradebook[gradebookId].students[studentIndex].course_grade;
            }

            if (state.gradebook[gradebookId].students.every(item => !item.course_grade)) {
                state.gradebook[gradebookId].publishedDate = null;
            }
        },

        EDIT_FIELD(state, { field, value }) {
            state[field] = value;
        },

        ADD_THREAD_DATA(state, { payload, type }) {
            state.submissions[type.data.activityId][type.data.submissionId].thread_data[type.name] = payload;
        },

        EDIT_SUBMISSION: (state, { activity_id, submission_id, submission }) => {
            state.submissions[activity_id][submission_id] = submission;
        },

        HYDRATE_SUBMISSIONS(state, payload) {
            state.submissions[payload.id] = {};

            payload.submissions.forEach(submission => {
                submission.thread_data = {};
                state.submissions[payload.id][submission.id] = submission;
            });
        },

        HYDRATE_GRADEBOOK(state, { id, payload }) {
            state.gradebook[id] = payload;
        },

        PURGE_SUBMISSIONS(state) {
            state.submissions = {};
        },

        PURGE_GRADEBOOK(state) {
            state.gradebook = {
                filterColumns: {
                    runningPercentage: { title: 'Running %', display: true },
                    percentage: { title: '%', display: true },
                    grade: { title: 'Grade', display: true },
                    comments: { title: 'Comments', display: true },
                    publish: { title: 'Publish', display: true },
                    admin: { title: 'Admin', display: true },
                },
                filterStudents: {
                    removed: { title: 'Include Removed', display: true },
                },
            };
        },

        PURGE_MISC_FILTERS(state) {
            state.miscFilters = {
                courseId: null,
                horizontalScroll: 0,
                searchValue: {
                    activity: '',
                    student: '',
                },
                searchActivity: false,
                searchStudent: false,
            };
        },

        PURGE_SELECTIONS(state) {
            state.selections = [];
        },

        UPDATE_SELECTIONS(state, selected) {
            state.selections = selected;
        },

        SET_ACTIVITY_GRADER_PARENT(state, { type }) {
            state.parent = type;
        },

        TOGGLE_LOADING(state) {
            state.isLoading = !state.isLoading;
        },

        TOGGLE_LOADER(state, id) {
            if (state.loaders.includes(id)) {
                state.loaders = state.loaders.filter(loader => loader !== id);
            } else {
                state.loaders.push(id);
            }
        },

        TOGGLE_EDITING(state, { activity_id, submission_id }) {
            state.submissions[activity_id][submission_id].is_editing =
                !state.submissions[activity_id][submission_id].is_editing;
        },

        EDIT_FILTERS(state, { path, property, value }) {
            const segments = path.split('.');
            const lastSegment = segments.pop();
            const field = segments.reduce((acc, curr) => acc[curr], state);

            field[lastSegment][property] = value;
        },

        EDIT_MISC_FILTERS(state, { field, value }) {
            state.miscFilters[field] = value;
        },

        SET_CANCEL_SOURCE: (state, cancel) => {
            state.cancelTokens.push(cancel);
        },

        EDIT_STUDENT_GRADE: (state, { student_index, grade_index, activity_grade }) => {
            // force state update
            const newState = cloneDeep(state);

            // overwrite existing grade with new grade
            set(
                newState,
                `gradebook.${activity_grade.gradebook_id}.students[${student_index}].user.grades[${grade_index}]`,
                activity_grade
            );

            const student = get(state, `gradebook.${activity_grade.gradebook_id}.students[${student_index}]`);

            // update total points for student
            set(
                newState,
                `gradebook.${activity_grade.gradebook_id}.students[${student_index}].total_points`,
                totalScore(student.user.grades, 'points')
            );

            set(state, 'gradebook', newState.gradebook);
        },

        UPDATE_ACTIVITY_POINTS(state, payload) {
            applyActivityPointsUpdates(state, payload);
        },
    },
};
